import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _memoize from "lodash/memoize";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { MarketplaceAnalyticsContext } from '@atlassian/mpac-analytics';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { TwoClickAppInstallFocusedTask } from '@atlassian/mpac-installation';
import { OneClickAppInstallDialog } from '@atlassian/mpac-site-selection';
import { AppListingTab } from '@atlassian/mpac-types';
import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import { AppHeader } from '@atlassian/mpac-ui';
import { convertToAtlassianHostingType, MarketplaceAppUtils, UrlUtils } from '@atlassian/mpac-utils';
import { useGetPartnerTrustCenterUrl } from '../../hooks/useGetPartnerTrustCenterUrl';
import { makeAppHeaderProps } from '../../utils';
import { AnimationContainer, HostingSelectContainerV2, ListingContainer } from './AppListing.styles';
import transformAppListing, { getAppIdentifier } from './AppListing.transform';
import { compatibleWithCloudSupportedProduct } from './AppListing.utils';
import { makeHeaderLozenges } from './HeaderLozenges';
import { HostingSelectV2 } from './HostingSelect';
import { makeAppListingTabs } from './makeAppListingTabs';
import ProductCompatibilitySummary from './ProductCompatibilitySummary';
import { AppStructuredData } from './StructuredData';
import AppListingV2 from './v2/AppListingV2';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const selectedTabWithFallback = (tabs, selectedTab, onTabSelect) => {
  const tabSelected = tabs.find(tab => tab.key === selectedTab);

  // If we didn't find the selected tab key in the current list of available
  // tabs, fallback to the first tab, and notify that tab has changed
  if (!tabSelected) {
    onTabSelect(tabs[0].key);
    return tabs[0];
  }
  return tabSelected;
};

// This is to stop recreating the same array(different instances) of compatible products which is re-rendering the child components
const mapCompatibleProducts = _memoize(cloudCompatibleProducts => cloudCompatibleProducts.map(product => product.id), cloudCompatibleProducts => cloudCompatibleProducts);
/* eslint-disable complexity */
// eslint-disable-next-line max-lines-per-function
const AppListing = props => {
  var _props$appDetails$par, _props$appDetails, _props$appDetails$par2, _appListing$programs, _appListing$programs$, _props$components, _props$components2, _appListing$programs2, _appListing$programs3, _appListing$programs4;
  const {
    showNewJiraSoftwareLabel,
    enableMediaFromProductListing,
    enableSelfServeAppEditions,
    enableAppReviewSummaryFromStoreBff,
    enableIsolatedCloudChanges,
    enableFedrampChanges
  } = useStatsig();
  const showNewJiraSoftwareLabelFlag = showNewJiraSoftwareLabel();
  const vendorId = (_props$appDetails$par = (_props$appDetails = props.appDetails) === null || _props$appDetails === void 0 ? void 0 : (_props$appDetails$par2 = _props$appDetails.partner) === null || _props$appDetails$par2 === void 0 ? void 0 : _props$appDetails$par2.id) !== null && _props$appDetails$par !== void 0 ? _props$appDetails$par : '';
  const {
    partnerTrustCenterUrl
  } = useGetPartnerTrustCenterUrl(vendorId);
  if (!props.appDetails) return null;
  const {
    appDetails,
    availableSiteCloudIds,
    editions,
    reviewSummaryDetails,
    appDetailsFromSearchResponse,
    links,
    headerInjection,
    selectedHosting,
    selectedTab,
    onTabSelect,
    mpacBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
    cloudMigration,
    loginHref,
    user,
    onHostingSelectOpen,
    onHostingOptionClick,
    onSupportLinkClick = () => null,
    onMediaViewerOpen = () => null,
    onWatchAppToggle = () => null,
    onSeeMoreReviewsClick = () => null,
    onArtifactDownloadClick = () => null,
    onContactPartnerClick = () => null,
    onCloudMigrationResourceLinksClick = () => null,
    onMigrationLozengeClick = () => null,
    onPricingCalculatorInstallCTAClick = () => null,
    showHeaderLozenges = false,
    showHeaderProductCompatibilities = false,
    showInstallationCount = false,
    showInstallationTab = false,
    serverEolStartDate = '',
    showIntegrationsDetailsSection = false,
    showPricingTab = false,
    showResourcesSection = false,
    showReviewsTab = false,
    showVersionsTab = false,
    showCloudMigrationLozenge = false,
    openMarketplaceLinksInNewTab = true,
    feedbackGatewayBaseUrl,
    feedbackCollectorApiKeys,
    isServerAppEos,
    oneClickAppInstallDialog,
    twoClickAppInstallFocusedTask,
    billingSystem,
    hostPricing,
    eligibleOfferings,
    eligibleForFreeOnSandbox,
    installedEdition,
    isSandbox
  } = props;
  const appListing = transformAppListing({
    appDetails,
    reviewSummaryDetails,
    enableAppReviewSummaryFromStoreBff: enableAppReviewSummaryFromStoreBff(),
    appDataFromSearch: appDetailsFromSearchResponse === null || appDetailsFromSearchResponse === void 0 ? void 0 : appDetailsFromSearchResponse.appData,
    mediaImageLink: links.mediaImage,
    mediaImageFromProductListingLink: links.mediaImageFromProductListing,
    partnerDetailsLink: links.partnerDetails,
    appVersionHistoryLink: links.appVersionHistory,
    artifactDownloadLink: links.artifactDownload,
    descriptorDownloadLink: links.descriptorDownload,
    licensePurchaseLink: links.licensePurchase,
    trialLicenseActivationLink: links.trialLicenseActivation,
    appIconLink: links.appIcon,
    hosting: selectedHosting,
    enableMediaFromProductListing: enableMediaFromProductListing(),
    enableIsolatedCloudChanges: enableIsolatedCloudChanges(),
    enableFedrampChanges: enableFedrampChanges()
  });
  const appCompatibleWithDcEnabledProduct = MarketplaceAppUtils.maybeDCCompatible(appListing.productHostingOptions.map(option => convertToAtlassianHostingType(option)), appListing.serverCompatibleProducts);
  const appCompatibleWithCloudSupportedProduct = compatibleWithCloudSupportedProduct(appListing.compatibleProducts);
  const showServerEolMessage = selectedHosting === AtlassianProductHostingType.Server;
  const alternateCloudListingLink = cloudMigration !== null && cloudMigration !== void 0 && cloudMigration.alternateCloudAppKey ? links.appDetails.expand({
    appKey: cloudMigration.alternateCloudAppKey
  }) : null;
  let hostingSelect;
  const appPricingDetailsLink = links.appPricingDetails.expand({
    appKey: appDetails.appKey
  });
  if (props.showHostingSelect) {
    hostingSelect = /*#__PURE__*/_jsx(HostingSelectContainerV2, {
      children: /*#__PURE__*/_jsx(HostingSelectV2, {
        productHostingOptions: appListing.productHostingOptions,
        selected: props.selectedHosting,
        appCompatibleWithDcEnabledProduct: appCompatibleWithDcEnabledProduct,
        appCompatibleWithCloudSupportedProduct: appCompatibleWithCloudSupportedProduct,
        onChange: props.onHostingChange,
        partnerSupportUrl: appListing.partner.support.url,
        alternateCloudListingLink: alternateCloudListingLink,
        onContactPartnerClick: onContactPartnerClick,
        onOpen: onHostingSelectOpen,
        onHostingOptionClick: onHostingOptionClick
      })
    });
  }
  const handleTabChange = tabKey => {
    if (typeof onTabSelect === 'function') {
      onTabSelect(tabKey);
    }
  };
  const isAppCloudFortified = !!((_appListing$programs = appListing.programs) !== null && _appListing$programs !== void 0 && (_appListing$programs$ = _appListing$programs.cloudFortified) !== null && _appListing$programs$ !== void 0 && _appListing$programs$.isStatusApproved);
  const hasCloudFortifiedBadge = isAppCloudFortified;
  const appHeaderDetails = makeAppHeaderProps(appListing, appDetailsFromSearchResponse);
  const appRating = appHeaderDetails.ratings;
  const tabs = makeAppListingTabs({
    appListing,
    availableSiteCloudIds,
    editions,
    selectedHosting,
    showInstallationTab,
    showIntegrationsDetailsSection,
    showResourcesSection,
    showCertificationSection: hasCloudFortifiedBadge,
    showReviewsTab,
    showVersionsTab,
    showPricingTab,
    mpacBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
    loginHref,
    user,
    onSupportLinkClick,
    onMediaViewerOpen,
    onWatchAppToggle,
    onSeeMoreReviewsClick,
    onArtifactDownloadClick,
    cloudMigration,
    onCloudMigrationResourceLinksClick,
    onPricingCalculatorInstallCTAClick,
    appRating,
    showServerEolMessage: !!showServerEolMessage,
    serverEolStartDate,
    feedbackCollectorApiKeys,
    feedbackGatewayBaseUrl,
    isServerAppEos,
    onTabSelect,
    appPricingDetailsLink,
    isEditionsSelfServeEnabled: enableSelfServeAppEditions(),
    billingSystem,
    trustCenterUrl: partnerTrustCenterUrl,
    hostPricing,
    eligibleOfferings,
    eligibleForFreeOnSandbox,
    installedEdition,
    isSandbox
  });
  const tabSelected = selectedTabWithFallback(tabs, selectedTab, onTabSelect);
  const supportTabLink = UrlUtils.updateQueryParam(window.location.href, 'tab', AppListingTab.Support);
  const iconLink = {
    expand: () => appListing.logo
  };
  const Container = ListingContainer;
  const compatibleProducts = mapCompatibleProducts(appListing.cloudCompatibleProducts);
  return /*#__PURE__*/_jsx(MarketplaceAnalyticsContext, {
    properties: {
      source: 'appListingScreen'
    },
    attributes: {
      deploymentType: appListing.deploymentType,
      hasCloudFortifiedBadge
    },
    children: /*#__PURE__*/_jsxs(Container, {
      children: [/*#__PURE__*/_jsx(AnimationContainer, {
        children: (_props$components = props.components) === null || _props$components === void 0 ? void 0 : _props$components.beforeHeader
      }), /*#__PURE__*/_jsx(AppListingV2, {
        appHeaderDetails: appHeaderDetails,
        appRatingLink: links.appRating,
        appIconLink: iconLink,
        bottomSeparator: false,
        headerInjection: headerInjection,
        hostingSelect: hostingSelect,
        afterHeader: (_props$components2 = props.components) === null || _props$components2 === void 0 ? void 0 : _props$components2.afterHeader,
        isAppCloudFortified: isAppCloudFortified,
        showInstallationCount: showInstallationCount,
        openMarketplaceLinksInNewTab: openMarketplaceLinksInNewTab,
        bugBountyParticipant: (_appListing$programs2 = appListing.programs) === null || _appListing$programs2 === void 0 ? void 0 : _appListing$programs2.bugBountyParticipant,
        compatibilitySummary: showHeaderProductCompatibilities ? /*#__PURE__*/_jsx(ProductCompatibilitySummary, {
          cloudCompatibleProducts: appListing.cloudCompatibleProducts,
          serverCompatibleProducts: appListing.serverCompatibleProducts,
          dataCenterCompatibleProducts: appListing.dataCenterCompatibleProducts,
          totalVersionCount: appListing.totalVersionCount,
          versionHistoryUrl: appListing.versionHistoryUrl
        }) : null,
        lozenges: showHeaderLozenges ? makeHeaderLozenges({
          supportDetails: appListing.supportDetails,
          partnerName: appListing.partner.name,
          supportLink: supportTabLink,
          categories: appListing.categories,
          marketingLabels: appListing.marketingLabels,
          onMigrationLozengeClick,
          cloudMigration,
          showCloudMigrationLozenge,
          isServerAppEos,
          showNewJiraSoftwareLabelFlag
        }) : [],
        selectedTab: tabSelected.key,
        onTabChange: handleTabChange,
        tabsData: tabs
      }), /*#__PURE__*/_jsx(AppStructuredData, {
        name: appListing.name,
        createdAt: appListing.createdAt,
        description: appListing.summary,
        icon: appListing.logo,
        partner: {
          name: appListing.partner.name,
          url: appListing.partner.url
        },
        review: {
          rating: appListing.reviewSummary.rating,
          count: appListing.reviewSummary.count
        },
        compatibleProducts: appListing.compatibleProducts
      }), oneClickAppInstallDialog && /*#__PURE__*/_jsx(OneClickAppInstallDialog, _objectSpread(_objectSpread({}, oneClickAppInstallDialog), {}, {
        appDetails: {
          appId: appDetails.appId,
          appKey: appDetails.appKey,
          appName: appDetails.name,
          appIdentifier: getAppIdentifier(appDetails),
          partner: {
            name: appListing.partner.name,
            type: appListing.partner.partnerType
          },
          endUserLicenseAgreementUrl: appListing.endUserLicenseAgreementUrl,
          privacyPolicyUrl: appListing.privacyPolicyUrl,
          deploymentType: appListing.deploymentType,
          appScopes: appListing.appScopes,
          paymentModel: appListing.paymentModel
        },
        appHeader: /*#__PURE__*/_jsx(AppHeader, {
          appHeaderDetails: appHeaderDetails,
          appIconLink: iconLink,
          appRatingLink: links.appRating,
          appIconSize: "small",
          showInstallationCount: true,
          isAppCloudFortified: isAppCloudFortified,
          bugBountyParticipant: (_appListing$programs3 = appListing.programs) === null || _appListing$programs3 === void 0 ? void 0 : _appListing$programs3.bugBountyParticipant
        }),
        mpacBaseUrl: mpacBaseUrl,
        links: links,
        compatibleProducts: appListing.cloudCompatibleProducts.map(product => product.id)
      })), twoClickAppInstallFocusedTask && /*#__PURE__*/_jsx(TwoClickAppInstallFocusedTask, _objectSpread(_objectSpread({}, twoClickAppInstallFocusedTask), {}, {
        appDetails: {
          appId: appDetails.appId,
          appKey: appDetails.appKey,
          appName: appDetails.name,
          appIdentifier: getAppIdentifier(appDetails),
          partner: {
            name: appListing.partner.name,
            type: appListing.partner.partnerType
          },
          endUserLicenseAgreementUrl: appListing.endUserLicenseAgreementUrl,
          privacyPolicyUrl: appListing.privacyPolicyUrl,
          deploymentType: appListing.deploymentType,
          paymentModel: appListing.paymentModel,
          isAppCloudFortified,
          bugBountyParticipant: (_appListing$programs4 = appListing.programs) === null || _appListing$programs4 === void 0 ? void 0 : _appListing$programs4.bugBountyParticipant,
          appScopes: appListing.appScopes,
          appIconLink: iconLink,
          buildNumber: appListing.buildNumber,
          version: appListing.version
        },
        appHeaderDetails: appHeaderDetails,
        mpacBaseUrl: mpacBaseUrl,
        links: links,
        compatibleProducts: compatibleProducts
      }))]
    })
  });
};
export default AppListing;